import { motion } from 'framer-motion';
import { springs } from '../lib/animations';

interface Strain {
  name: string;
  type?: string;
  effects?: string[];
  flavors?: string[];
  description?: string;
  rating?: number;
  image_url?: string;
  medicalUses?: string[];
  negatives?: string[];
}

interface StrainDetailModalProps {
  strain: Strain;
  onClose: () => void;
}

export default function StrainDetailModal({ strain, onClose }: StrainDetailModalProps) {
  const typeBg = strain.type === 'Sativa'
    ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
    : strain.type === 'Indica'
    ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
    : 'bg-cannabis-500/20 text-cannabis-300 border-cannabis-500/30';

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        className="relative bg-cannabis-800 border border-cannabis-700 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={springs.subtle}
      >
        {/* Header */}
        <div className="sticky top-0 bg-cannabis-800 border-b border-cannabis-700 px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <h2 className="text-cream font-bold text-lg">{strain.name}</h2>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${typeBg}`}>
              {strain.type || 'Hybrid'}
            </span>
          </div>
          <button onClick={onClose} className="text-cream/40 hover:text-cream text-2xl leading-none transition">×</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Image */}
          {strain.image_url && (
            <div className="w-full h-48 rounded-xl overflow-hidden bg-cannabis-900">
              <img src={strain.image_url} alt={strain.name} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Rating */}
          {strain.rating != null && (
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map(i => (
                  <span key={i} className={`text-lg ${i <= Math.round(strain.rating! / 2) ? 'text-yellow-400' : 'text-cream/20'}`}>★</span>
                ))}
              </div>
              <span className="text-cream/50 text-sm">{strain.rating.toFixed(1)} / 10</span>
            </div>
          )}

          {/* Description */}
          {strain.description && (
            <div>
              <h3 className="text-cream/60 text-xs font-semibold uppercase tracking-wider mb-1">Description</h3>
              <p className="text-cream/80 text-sm leading-relaxed">{strain.description}</p>
            </div>
          )}

          {/* Effects */}
          {Array.isArray(strain.effects) && strain.effects.length > 0 && (
            <div>
              <h3 className="text-cream/60 text-xs font-semibold uppercase tracking-wider mb-2">Effects</h3>
              <div className="flex flex-wrap gap-1.5">
                {strain.effects.map((eff, i) => {
                  const effStr = typeof eff === 'string' ? eff : String(eff);
                  return <span key={`${effStr}-${i}`} className="text-xs bg-cannabis-600/30 text-cannabis-300 px-2.5 py-1 rounded-full">{effStr}</span>;
                })}
              </div>
            </div>
          )}

          {/* Flavors */}
          {Array.isArray(strain.flavors) && strain.flavors.length > 0 && (
            <div>
              <h3 className="text-cream/60 text-xs font-semibold uppercase tracking-wider mb-2">Flavors</h3>
              <div className="flex flex-wrap gap-1.5">
                {strain.flavors.map((flav, i) => {
                  const flavStr = typeof flav === 'string' ? flav : String(flav);
                  return <span key={`${flavStr}-${i}`} className="text-xs bg-amber-500/15 text-amber-300 px-2.5 py-1 rounded-full">{flavStr}</span>;
                })}
              </div>
            </div>
          )}

          {/* Medical Uses */}
          {strain.medicalUses && strain.medicalUses.length > 0 && (
            <div>
              <h3 className="text-cream/60 text-xs font-semibold uppercase tracking-wider mb-2">Medical Uses</h3>
              <div className="flex flex-wrap gap-1.5">
                {strain.medicalUses.map(use => (
                  <span key={use} className="text-xs bg-blue-500/15 text-blue-300 px-2.5 py-1 rounded-full">{use}</span>
                ))}
              </div>
            </div>
          )}

          {/* Negatives */}
          {strain.negatives && strain.negatives.length > 0 && (
            <div>
              <h3 className="text-cream/60 text-xs font-semibold uppercase tracking-wider mb-2">Negatives</h3>
              <div className="flex flex-wrap gap-1.5">
                {strain.negatives.map(neg => (
                  <span key={neg} className="text-xs bg-red-500/15 text-red-300 px-2.5 py-1 rounded-full">{neg}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
